import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isApiError, refusalMessage } from '../api';
import { clientRatings } from '../api/ratings';
import { clearCustomerSession } from '../lib/customerSession';
import './chatNotifyRating.css';

const DRIVER_LABELS = ['', 'Very Poor', 'Poor', 'Average', 'Good', 'Excellent'];
const MAX_CH = 200;

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M15.5 19.5L8 12l7.5-7.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Star({ on, onClick, label }) {
  return (
    <button
      type="button"
      className="ingRate__starB"
      onClick={onClick}
      aria-label={label}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 2.2l1.5 4.2h4.2l-3.4 2.6L15.8 14L12 11.5L8.2 14l1.2-3.7L5.3 7.3h4.2L10.8 3.1z"
          stroke="#ec6c23"
          strokeWidth="1.1"
          strokeLinejoin="round"
          fill={on ? '#ec6c23' : 'none'}
        />
      </svg>
    </button>
  );
}

function StarRow({ value, onChange, name }) {
  return (
    <div className="ingRate__stars" role="group" aria-label={name}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          on={i <= value}
          onClick={() => onChange(i)}
          label={`${i} ${i === 1 ? 'star' : 'stars'}`}
        />
      ))}
    </div>
  );
}

/**
 * The client rates the rider who carried their delivery (ingo-e0, 2 Oct
 * 2026; InGo ADR 0029): `/rate/:orderId`, the delivery's id, opened from its
 * completed tracking screen. One to five stars, the quick-feedback chips in
 * the server's words, and a note for the office, which the rider never sees.
 * Once per delivery, never changed; a second try is read as sent.
 */
function ApiRateExperience({ ratings }) {
  const navigate = useNavigate();
  const { orderId = '' } = useParams();
  const [about, setAbout] = useState(null);
  const [stars, setStars] = useState(0);
  const [tags, setTags] = useState(() => new Set());
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const done = useCallback(() => navigate('/orders', { replace: true }), [navigate]);

  useEffect(() => {
    let cancelled = false;
    ratings.state(orderId).then(
      (answer) => !cancelled && setAbout(answer),
      (error) => {
        if (cancelled) return;
        if (isApiError(error) && error.status === 401) {
          clearCustomerSession();
          navigate('/login', { replace: true });
          return;
        }
        setErr(refusalMessage(error));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [orderId, ratings, navigate]);

  const toggleTag = (key) => {
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const submit = async () => {
    setErr('');
    if (stars < 1 || stars > 5) {
      setErr('Please add a star rating.');
      return;
    }
    setBusy(true);
    try {
      await ratings.rate(orderId, { stars, tags: [...tags], comment: text });
      done();
    } catch (error) {
      if (isApiError(error) && error.code === 'ALREADY_RATED') {
        done();
        return;
      }
      setErr(refusalMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const given = about?.rating ?? null;
  const closed = about !== null && !about.may_rate;

  return (
    <div className="ingRate ingRate--centered" role="main" aria-label="Rate your experience">
      <div className="ingRate__centerWrap">
        <div className="ingRate__centerCard">
          <header className="ingRate__h ingRate__h--card">
            <button type="button" className="ingRate__back" onClick={() => navigate(-1)} aria-label="Back">
              <BackIcon />
            </button>
            <h1 className="ingRate__title">Rate Your Experience</h1>
          </header>

          <div className="ingRate__sc ingRate__sc--card">
            {about ? (
              <div className="ingRate__card">
                <p className="ingRate__oid">{about.delivery.reference}</p>
                <p className="ingRate__addr">
                  From: {about.delivery.pickup}
                  <br />
                  To: {about.delivery.dropoff}
                </p>
                {about.name ? (
                  <div className="ingRate__drow">
                    <div className="ingRate__dAv" aria-hidden />
                    <p className="ingRate__dN">{about.name}</p>
                  </div>
                ) : null}
              </div>
            ) : null}

            {closed ? (
              <>
                <p className="ingRate__sub" role="status">
                  {given ? `You rated this delivery ${given.stars} / 5. A rating cannot be changed.` : about.refusal}
                </p>
                <button type="button" className="ingRate__submit" onClick={() => navigate(-1)}>
                  Back
                </button>
              </>
            ) : (
              <>
                <h2 className="ingRate__secT">How was your rider?</h2>
                <StarRow value={stars} onChange={setStars} name="Rider rating" />
                {stars > 0 ? (
                  <p className="ingRate__sub" aria-live="polite">
                    {DRIVER_LABELS[stars]}
                  </p>
                ) : null}
                {about?.tags?.length ? (
                  <>
                    <h2 className="ingRate__secT" style={{ marginTop: 12 }}>
                      Quick feedback
                    </h2>
                    <div className="ingRate__tags">
                      {about.tags.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          aria-pressed={tags.has(t.key)}
                          className={tags.has(t.key) ? 'ingRate__tag ingRate__tag--on' : 'ingRate__tag'}
                          onClick={() => toggleTag(t.key)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}

                <h2 className="ingRate__secT">Written feedback</h2>
                <textarea
                  className="ingRate__ta"
                  rows={3}
                  maxLength={MAX_CH}
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, MAX_CH))}
                  placeholder="Only the office reads this."
                  aria-label="Written feedback"
                />
                <div className="ingRate__taC">
                  {text.length}/{MAX_CH}
                </div>

                {err ? (
                  <p className="auth-message auth-message--error" role="alert">
                    {err}
                  </p>
                ) : null}
                <button type="button" className="ingRate__submit" onClick={submit} disabled={busy || about === null}>
                  {busy ? 'Submitting...' : 'Submit Review'}
                </button>
                <button type="button" className="ingRate__skip" onClick={() => navigate(-1)}>
                  Skip for now
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** An InGo client rates on the API; a session of the old system keeps the old page. */
export default function RateExperiencePage({ ratings = clientRatings }) {
  return <ApiRateExperience ratings={ratings} />;
}

